import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth/auth';
import { StateStoreService } from '../services/state-store/state-store';
import {
  PAYMENT_METHOD_RETURN_PARAM,
  cardSetupReturnParams,
} from '../utils/billing-readiness.utils';

/**
 * Redirects to `/complete-profile` when the signed-in user is missing terms
 * acceptance, name fields, an organisation, a complete billing address
 * (VER-349) or a saved payment method (VER-353). Anonymous users fall
 * through — `authGuard` owns that redirect.
 *
 * Readiness is guaranteed by `appReadyMatch` at the route level.
 *
 * `requiresBillingDetails` is passed through rather than re-derived here.
 * `ProfileCompletionComponent.initializeSteps()` reads the same computed to
 * decide which billing steps to show, and if the two ever disagreed this
 * guard would redirect to `/complete-profile`, the component would find
 * nothing to complete and redirect back, and the browser would hang.
 *
 * **Billing is part of completion.** The address and the card are both
 * mandatory, and unlike the other three they can be outstanding on an
 * account that already finished the wizard — someone who closed the tab
 * midway, or who signed up before the requirement existed. Checking only
 * `needsProfileCompletion`'s other inputs would let those accounts stay in
 * the app forever with no route back to the steps they owe.
 *
 * Its payment-method half fails open (see `requiresPaymentMethod`):
 * unknown, in-flight and failed reads all answer false. That is deliberate
 * — this guard runs on every navigation, so a term that went true on a
 * Stripe blip would trap every user in onboarding.
 */
export const profileCompletionGuard: CanActivateFn = (route, snapshot) => {
  const auth = inject(AuthService);
  const state = inject(StateStoreService);
  const router = inject(Router);

  if (!auth.isAuthenticated()) return true;

  const hasOrg = !!state.selectedOrgId();
  if (!auth.needsProfileCompletion(hasOrg, state.requiresBillingDetails())) return true;

  return router.createUrlTree(['/complete-profile'], {
    queryParams: {
      returnUrl: snapshot.url,
      // Forward the Stripe card-setup outcome. Setup sessions always
      // return to /settings/billing now, so this is the hop that carries
      // a mid-onboarding card back to the wizard — see
      // `cardSetupReturnParams`.
      ...cardSetupReturnParams(route.queryParamMap.get(PAYMENT_METHOD_RETURN_PARAM)),
    },
  });
};
